import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiService, MarkerRecord } from '../../services/api';
import { segmentCache } from '../../services/segmentCache';
import {
  getPreviewMediaCandidates,
  getThumbnailPreviewSource,
} from '../../utils/mediaSources';
import MarkedTile, { PreviewData } from './MarkedTile';
import { GroupBy } from './MarkedTabControls';

interface MarkedGridProps {
  records: MarkerRecord[];
  tilesPerRow: number;
  showDetails: boolean;
  groupBy: GroupBy;
  onRecordClick: (record: MarkerRecord) => void;
  onRecordRemove: (record: MarkerRecord) => void;
  onRecordChange: (previous: MarkerRecord, next: MarkerRecord | null) => void;
}

interface GroupedRecords {
  label: string;
  records: MarkerRecord[];
}

const formatEntityTypeLabel = (type: string): string => {
  switch (type) {
    case 'video':
      return 'Videos';
    case 'segment':
      return 'Segments';
    case 'metadata_field':
      return 'Metadata Fields';
    case 'prompt_run':
      return 'Prompt Runs';
    default:
      return type;
  }
};

const groupRecords = (records: MarkerRecord[], groupBy: GroupBy): GroupedRecords[] => {
  if (groupBy === 'none') {
    return [{ label: '', records }];
  }

  const groups = new Map<string, MarkerRecord[]>();

  records.forEach((record) => {
    let key: string;
    if (groupBy === 'type') {
      key = record.entity_type;
    } else if (groupBy === 'video') {
      key = record.video_id || 'Unknown Video';
    } else {
      key = '';
    }

    const existing = groups.get(key) || [];
    existing.push(record);
    groups.set(key, existing);
  });

  const result: GroupedRecords[] = [];
  groups.forEach((recs, key) => {
    result.push({
      label: groupBy === 'type' ? formatEntityTypeLabel(key) : key,
      records: recs,
    });
  });

  // Sort groups alphabetically
  result.sort((a, b) => a.label.localeCompare(b.label));

  return result;
};

const MarkedGrid: React.FC<MarkedGridProps> = ({
  records,
  tilesPerRow,
  showDetails,
  groupBy,
  onRecordClick,
  onRecordRemove,
  onRecordChange,
}) => {
  const [previewData, setPreviewData] = useState<Map<string, PreviewData>>(new Map());
  const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadedRef = useRef<Set<string>>(new Set());
  const tileRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const loadPreviewForMarker = useCallback(async (record: MarkerRecord) => {
    if (loadedRef.current.has(record.marker_id)) {
      return;
    }
    loadedRef.current.add(record.marker_id);

    const videoId = record.video_id;
    if (!videoId) {
      return;
    }

    try {
      if (record.entity_type === 'video') {
        const video = await apiService.getVideo(videoId);
        const thumbnailSource = getThumbnailPreviewSource(video);

        if (thumbnailSource || video.title) {
          setPreviewData((prev) => {
            const next = new Map(prev);
            next.set(record.marker_id, {
              thumbnailUrl: thumbnailSource || undefined,
              thumbnailGcsUri: video.thumbnail_gcs_uri || undefined,
              videoTitle: video.title,
            });
            return next;
          });
        }
        return;
      }

      const runId = record.run_id || null;
      const segments = await segmentCache.loadSegments(videoId, runId);

      let segment = record.segment_id
        ? segments.find((s) => s.segment_id === record.segment_id)
        : segments[0];

      if (segment) {
        const previewCandidates = getPreviewMediaCandidates(segment);
        const exactThumbnail = previewCandidates.find(
          (candidate) => candidate.kind === 'thumbnail',
        );
        const exactGif = previewCandidates.find(
          (candidate) => candidate.kind === 'gif',
        );
        // The still surface may fall back to an available GIF, but every
        // immediate representation must keep the durable authority for that
        // exact asset. The hover surface falls back to the still naturally in
        // MarkedSegmentTile and therefore does not need a cross-kind alias.
        const stillCandidate = exactThumbnail || exactGif;
        const startTime = segment.start_time;
        const endTime = segment.end_time;

        setPreviewData((prev) => {
          const next = new Map(prev);
          next.set(record.marker_id, {
            thumbnailUrl: stillCandidate?.source,
            gifUrl: exactGif?.source,
            thumbnailGcsUri: stillCandidate?.durableSource || undefined,
            gifGcsUri: exactGif?.durableSource || undefined,
            segmentStartTime: startTime,
            segmentEndTime: endTime,
          });
          return next;
        });
      }
    } catch (error) {
      // Clear the loaded flag so the marker can retry on subsequent intersections
      loadedRef.current.delete(record.marker_id);
      console.warn(`Failed to load preview for marker ${record.marker_id}:`, error);
    }
  }, []);

  // Set up intersection observer for lazy loading
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const markerId = entry.target.getAttribute('data-marker-id');
            if (markerId) {
              const record = records.find((r) => r.marker_id === markerId);
              if (record) {
                loadPreviewForMarker(record);
              }
            }
          }
        });
      },
      {
        rootMargin: '200px',
        threshold: 0,
      }
    );

    return () => {
      observerRef.current?.disconnect();
    };
  }, [records, loadPreviewForMarker]);

  // Observe tile elements
  useEffect(() => {
    const observer = observerRef.current;
    if (!observer) return;

    tileRefs.current.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, [records]);

  const setTileRef = useCallback((markerId: string, element: HTMLDivElement | null) => {
    if (element) {
      tileRefs.current.set(markerId, element);
      observerRef.current?.observe(element);
    } else {
      const existing = tileRefs.current.get(markerId);
      if (existing) {
        observerRef.current?.unobserve(existing);
        tileRefs.current.delete(markerId);
      }
    }
  }, []);

  const groupedRecords = groupRecords(records, groupBy);
  const gridStyle = {
    gridTemplateColumns: `repeat(${tilesPerRow}, minmax(0, 1fr))`,
  };

  return (
    <div className={`marked-grid-container ${groupBy !== 'none' ? 'grouped' : ''}`}>
      {groupedRecords.map((group, groupIndex) => (
        <div key={group.label || groupIndex} className="marked-group">
          {group.label && (
            <div className="marked-group-label">
              <span>{group.label}</span>
            </div>
          )}
          <div
            className={`marked-grid ${showDetails ? 'show-details' : ''}`}
            style={gridStyle}
          >
            {group.records.map((record) => (
              <div
                key={record.marker_id}
                ref={(el) => setTileRef(record.marker_id, el)}
                data-marker-id={record.marker_id}
              >
                <MarkedTile
                  record={record}
                  previewData={previewData.get(record.marker_id)}
                  isHovered={hoveredMarkerId === record.marker_id}
                  onHover={(hovered) => setHoveredMarkerId(hovered ? record.marker_id : null)}
                  onClick={() => onRecordClick(record)}
                  onRemove={() => onRecordRemove(record)}
                  onMarkerChange={(next) => onRecordChange(record, next)}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default MarkedGrid;
